// frontend/src/pages/UserAvisosPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useApi from '../hooks/useApi';
import { Bell, Check, History, Info, AlertTriangle, CheckCircle, Volume2, VolumeX, BellOff } from 'lucide-react';

const TIPO_AVISO_CLASSES = {
    info: 'bg-blue-100 border-blue-500 text-blue-800',
    warning: 'bg-yellow-100 border-yellow-500 text-yellow-800',
    error: 'bg-red-100 border-red-500 text-red-800',
    success: 'bg-green-100 border-green-500 text-green-800',
};

const TIPO_AVISO_ICONE = {
    info: <Info className="text-blue-500" size={20} />,
    warning: <AlertTriangle className="text-yellow-500" size={20} />,
    error: <AlertTriangle className="text-red-500" size={20} />,
    success: <CheckCircle className="text-green-500" size={20} />,
};

// Componente para renderizar um único aviso na lista
const AvisoItem = ({ aviso, onMarkAsRead, isRead }) => {
    
    // --- FUNÇÃO DE FORMATAÇÃO INTELIGENTE (AUTO-CORREÇÃO DE FUSO) ---
    const formatDateLocal = (dateString) => {
        if (!dateString) return '-';
        
        try {
            // Tenta converter para Date (aceita ISO, SQL com T, etc.)
            let dateToParse = dateString;
            // Se for SQL simples "2025-12-15 17:12:26", substitui espaço por T
            if (dateString.includes(' ') && !dateString.includes('T')) {
                dateToParse = dateString.replace(' ', 'T'); 
            }

            let dateObj = new Date(dateToParse);
            if (isNaN(dateObj)) return dateString; // Falha no parse

            // --- LÓGICA DE CORREÇÃO INTELIGENTE ---
            const now = new Date();
            // Calcula diferença em horas entre a data do aviso e agora
            const diffHours = (dateObj - now) / (1000 * 60 * 60);

            // Se a data do aviso estiver mais de 1 hora NO FUTURO (ex: 17h quando é 14h),
            // assume que veio como UTC errado e subtrai 3 horas.
            if (diffHours > 1) {
                dateObj.setHours(dateObj.getHours() - 3);
            }

            // Formata para exibição final (DD/MM/YYYY, HH:mm:ss)
            return dateObj.toLocaleString('pt-BR');

        } catch (e) {
            return dateString; // Se falhar, mostra como veio
        }
    };

    const renderMessage = () => {
        let message = aviso.message;
        if (aviso.is_system_generated) {
            message = message
                .replace(/{pet_name}/g, `<strong class="text-blue-600">${aviso.pet_name || ''}</strong>`)
                .replace(/{tutor_name}/g, `<strong class="text-blue-600">${aviso.tutor_name || ''}</strong>`)
                .replace(/{days_left}/g, `<strong class="text-blue-600">${aviso.days_left || '0'}</strong>`);
        }
        return <p className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: message }}></p>;
    };

    return (
        <div className={`p-4 border-l-4 rounded-r-lg ${TIPO_AVISO_CLASSES[aviso.type] || TIPO_AVISO_CLASSES.info}`}>
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    {TIPO_AVISO_ICONE[aviso.type] || TIPO_AVISO_ICONE.info}
                    <div>
                        <h3 className="font-bold">{aviso.title}</h3>
                        {renderMessage()}
                        
                        {isRead ? (
                             <p className="text-xs text-gray-500 mt-2">Lido em: {formatDateLocal(aviso.read_at)}</p>
                        ) : (
                             <p className="text-xs text-gray-500 mt-2">Recebido em: {formatDateLocal(aviso.created_at)}</p>
                        )}
                    </div>
                </div>
                {!isRead && (
                    <button 
                        onClick={() => onMarkAsRead(aviso.id)}
                        className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-100 rounded-full hover:bg-green-200"
                        title="Marcar como lido"
                    >
                        <Check size={14} /> Marcar como Lido
                    </button>
                )}
            </div>
        </div>
    );
};

// --- Sub-componente: UserAlertSettings ---
const UserAlertSettings = ({ avisoSettings, onUpdateSettings }) => {
    const handleToggle = (key) => {
        const newValue = !avisoSettings[key];
        onUpdateSettings(key, newValue); 
    };

    const ToggleSwitch = ({ id, checked, onChange, label, icon: Icon }) => (
        <label htmlFor={id} className="flex items-center justify-between cursor-pointer p-2 rounded-md hover:bg-gray-50">
            <span className="flex items-center gap-3">
                <Icon size={18} className="text-gray-600" />
                <span className="font-medium text-gray-700">{label}</span>
            </span>
            <div className="relative">
                <input id={id} type="checkbox" className="sr-only" checked={checked} onChange={onChange} />
                <div className={`block w-10 h-6 rounded-full ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow transition-transform ${checked ? 'translate-x-4' : ''}`}></div>
            </div>
        </label>
    );
    
    return (
          <div className="p-4 mb-6 bg-white rounded-lg shadow">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Minhas Configurações de Aviso</h2>
              <div className="space-y-2">
                  <ToggleSwitch
                      id="sound-toggle"
                      checked={avisoSettings.playSound}
                      onChange={() => handleToggle('playSound')}
                      label="Ativar som para novos avisos"
                      icon={avisoSettings.playSound ? Volume2 : VolumeX}
                  />
                  <ToggleSwitch
                      id="popup-toggle"
                      checked={avisoSettings.showPopup}
                      onChange={() => handleToggle('showPopup')}
                      label="Ativar pop-up para novos avisos"
                      icon={avisoSettings.showPopup ? Bell : BellOff}
                  />
              </div>
          </div>
    );
};

// --- Componente Principal da Página ---
const UserAvisosPage = ({ onDataChange, avisoSettings, onUpdateSettings, onAvisoRefresh }) => {
    const { apiFetch, currentUser } = useApi();
    const [allAvisos, setAllAvisos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('ativos');
    
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        const url = '/avisos/my/all';
        console.log(`[DEBUG] UserAvisosPage: A buscar todos os avisos de: ${url}`);
        
        try {
            const data = await apiFetch(url);
            setAllAvisos(data);
        } catch (err) {
            console.error(`[DEBUG] UserAvisosPage: Erro ao buscar ${url}`, err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, refreshKey]);

    useEffect(() => {
        fetchData();
    }, [fetchData, onDataChange]);

    // 4. Separa os avisos com FILTRO EXTRA DE SEGURANÇA
    const { activeAvisos, readAvisos } = useMemo(() => {
        const userCreatedAt = currentUser?.created_at;

        // Função de segurança: Verifica se o aviso é realmente posterior ao usuário
        const isValidDate = (avisoDate) => {
            if (!userCreatedAt) return true; // Se usuário não tem data (legado), mostra tudo
            if (!avisoDate) return false;

            // Comparação de Strings "YYYY-MM-DD HH:mm:ss" funciona para filtro simples
            return avisoDate >= userCreatedAt;
        };

        const filteredList = allAvisos.filter(aviso => {
            const shouldShow = isValidDate(aviso.created_at);
            if (!shouldShow) {
                console.log(`[Filtro Frontend] Ocultando aviso ID ${aviso.id} (${aviso.created_at}) anterior ao usuário (${userCreatedAt})`);
            }
            return shouldShow;
        });

        return {
            activeAvisos: filteredList.filter(a => a.status === 'unread'),
            readAvisos: filteredList.filter(a => a.status === 'read')
        };
    }, [allAvisos, currentUser]);

    const handleMarkAsRead = async (avisoId) => {
        const url = `/avisos/my/${avisoId}/read`;
        console.log(`[DEBUG] UserAvisosPage: A marcar aviso como lido em: ${url}`);
        
        try {
            await apiFetch(url, { method: 'POST' });
            setRefreshKey(prev => prev + 1);
            if (onAvisoRefresh) {
                onAvisoRefresh();
            }
        } catch (err) {
            console.error(`[DEBUG] UserAvisosPage: Erro ao marcar como lido ${url}`, err);
            setError(`Erro ao marcar aviso como lido: ${err.message}`);
        }
    };

    // --- NOVA FUNÇÃO: MARCAR TODOS COMO LIDOS ---
    const handleMarkAllAsRead = async () => {
        const url = '/avisos/my/read-all';
        console.log(`[DEBUG] UserAvisosPage: A marcar todos os avisos como lidos em: ${url}`);
        
        try {
            await apiFetch(url, { method: 'POST' });
            setRefreshKey(prev => prev + 1);
            if (onAvisoRefresh) {
                onAvisoRefresh();
            }
        } catch (err) {
            console.error(`[DEBUG] UserAvisosPage: Erro ao marcar todos como lidos ${url}`, err);
            setError(`Erro ao marcar todos os avisos como lidos: ${err.message}`);
        }
    };

    const tabs = [
        { id: 'ativos', label: 'Avisos Ativos', icon: Bell },
        { id: 'lidos', label: 'Histórico de Leitura', icon: History }
    ];

    return (
        <div>
            <UserAlertSettings 
                avisoSettings={avisoSettings}
                onUpdateSettings={onUpdateSettings}
            />

            <h1 className="text-3xl font-bold text-gray-800">Meus Avisos</h1>
            <p className="mb-6 text-gray-600">Veja aqui todos os avisos direcionados a si.</p>
            
            {error && <p className="p-4 text-red-700 bg-red-100 rounded-md mb-4">{error}</p>}

            <div className="flex justify-between items-end border-b mb-6">
                <div className="flex">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            <tab.icon size={16} /> {tab.label}
                        </button>
                    ))}
                </div>
                
                {/* BOTÃO "MARCAR TODOS COMO LIDOS" */}
                {activeTab === 'ativos' && activeAvisos.length > 0 && (
                    <button 
                        onClick={handleMarkAllAsRead}
                        className="flex items-center gap-2 px-3 py-1.5 mb-1 mr-2 text-sm font-semibold text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        title="Marcar todos os avisos desta lista como lidos"
                    >
                        <CheckCircle size={16} /> Marcar todos como lidos
                    </button>
                )}
            </div>
            
            {loading && <p>A carregar avisos...</p>}
            
            {!loading && (
                <div className="space-y-4">
                    {/* Aba de Avisos Ativos */}
                    {activeTab === 'ativos' && (
                        <>
                            {activeAvisos.length === 0 && <p className="text-gray-500">Não há novos avisos.</p>}
                            {activeAvisos.map(aviso => (
                                <AvisoItem key={aviso.id} aviso={aviso} onMarkAsRead={handleMarkAsRead} isRead={false} />
                            ))}
                        </>
                    )}
                    
                    {/* Aba de Histórico */}
                    {activeTab === 'lidos' && (
                        <>
                            {readAvisos.length === 0 && <p className="text-gray-500">Não há avisos no seu histórico.</p>}
                            {readAvisos.map(aviso => (
                                <AvisoItem key={aviso.id} aviso={aviso} onMarkAsRead={() => {}} isRead={true} />
                            ))}
                        </>
                    )}
                </div>
            )}

        </div>
    );
};

export default UserAvisosPage;